import {useTranslation} from '@aboutyou/i18n';
import {RouterLinkTarget} from '@aboutyou/router';
import {Slide} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {useRef} from 'react';
import {COLORS} from '../../../constants/colors';
import {
  desktopAndUpLinaria,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useImageParallax} from '../../../hooks/useImageParallax/useImageParallax';
import {useInView} from '../../../hooks/useInView/useInView';
import {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {Image} from '../../atoms/Image/Image';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';

interface TeaserImageTileProps {
  testId?: string;
  href?: RouterLinkTarget | string;
  onClick?: React.MouseEventHandler;
  image?: ImageType;
  aspectRatio: `${number}:${number}`;
  content?: {
    headline?: string;
    subline?: string;
    image?: ImageType;
    cta?: string;
    additionalLinks?: {
      title: string;
      links: {
        path?: {url: string};
        label: string;
        TrackedLinkPromotionWrapper: React.FC<React.PropsWithChildren>;
        onClick?: React.MouseEventHandler;
      }[];
    };
    // specify if the content should centered or should be spaced
    justifyContent?: 'center' | 'space-between';
  };
  shouldWrapTeaserImageWithLink?: boolean;
  shouldWrapContentImageWithLink?: boolean;
  imageParallaxEffect?: boolean;
  className?: string;
  brandName?: string;
}

export const TeaserImageTile: React.FC<TeaserImageTileProps> = ({
  testId = 'teaserImage',
  href,
  onClick,
  image,
  aspectRatio,
  content,
  className,
  shouldWrapTeaserImageWithLink = true,
  shouldWrapContentImageWithLink = false,
  imageParallaxEffect = false,
  brandName = 'brand',
}) => {
  const backgroundWrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const {t} = useTranslation();

  const {ref: wrapperRef, inView} = useInView<HTMLImageElement>({
    threshold: 0.9,
    triggerOnce: true,
  });

  useImageParallax({wrapperRef, imageRef, disabled: !imageParallaxEffect});

  const teaserAnchorOnClick = onlyWhenAnchor(
    shouldWrapTeaserImageWithLink,
    onClick,
  );
  const contentAnchorOnClick = onlyWhenAnchor(
    shouldWrapContentImageWithLink,
    onClick,
  );

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      data-testid={`${testId}Wrapper`}
    >
      <StyledWrapper
        as={shouldWrapTeaserImageWithLink ? StyledLink : 'div'}
        to={href}
        onClick={teaserAnchorOnClick}
        data-testid={`${testId}Link`}
        aria-label={
          shouldWrapTeaserImageWithLink
            ? t('Go to the brand page: {brandName}', {
                brandName,
              })
            : undefined
        }
      >
        <BackgroundWrapper
          ref={backgroundWrapperRef}
          $fixedPosition={imageParallaxEffect}
        >
          {image && (
            <StyledImage
              alt={image.description}
              breakpoints={breakpoints}
              ratio={aspectRatio}
              disableFixedRatio={imageParallaxEffect}
              src={image.url}
              loading="lazy"
              widths={
                imageParallaxEffect
                  ? possibleImageWidths.map(width => width * 1.5)
                  : possibleImageWidths
              }
              data-testid={testId}
              ref={imageRef}
              $hasParallaxEffect={imageParallaxEffect}
            />
          )}
        </BackgroundWrapper>

        {content &&
        (content.headline ||
          content.subline ||
          content.image ||
          content.cta ||
          content.additionalLinks) ? (
          <TeaserTextWrapper>
            {content.subline && (
              <Slide mounted={inView} duration={500} direction={'up'}>
                <Subline data-testid="teaserSubline">{content.subline}</Subline>
              </Slide>
            )}

            {content.headline && (
              <ContentWrapper
                as={shouldWrapContentImageWithLink ? StyledLink : 'div'}
                to={href}
                onClick={contentAnchorOnClick}
                aria-label={
                  shouldWrapContentImageWithLink
                    ? t('Go to the brand page: {brandName}', {
                        brandName: content.headline,
                      })
                    : undefined
                }
              >
                <Slide
                  mounted={inView}
                  duration={500}
                  direction={'up'}
                  transformLength={20}
                >
                  <Headline data-testid="teaserHeadline">
                    {content.headline}
                  </Headline>
                </Slide>
              </ContentWrapper>
            )}

            {content.image && (
              <ContentWrapper
                as={shouldWrapContentImageWithLink ? StyledLink : 'div'}
                to={href}
                onClick={contentAnchorOnClick}
                aria-label={
                  shouldWrapContentImageWithLink
                    ? t('Go to the brand page: {brandName}', {
                        brandName,
                      })
                    : undefined
                }
              >
                <Slide
                  mounted={inView}
                  duration={1500}
                  direction={'up'}
                  transformLength={20}
                >
                  <StyledCenteredImage
                    src={content.image.url}
                    alt={content.image.description}
                    breakpoints={[]}
                    ratio="1:1"
                    widths={possibleLogoWidths}
                    data-testid="teaserLogoImage"
                  />
                </Slide>
              </ContentWrapper>
            )}

            {content.additionalLinks && (
              <Slide
                mounted={inView}
                duration={1500}
                direction={'up'}
                transformLength={20}
              >
                <AdditionalLinks data-testid="teaserAdditionalLinks">
                  <StyledAdditionalLinkSpan>
                    {content.additionalLinks.title}
                  </StyledAdditionalLinkSpan>
                  <LinkWrapper>
                    {content.additionalLinks.links.map(
                      (
                        {
                          path,
                          label,
                          TrackedLinkPromotionWrapper,
                          onClick: itemOnClick,
                        },
                        index,
                      ) => {
                        return path ? (
                          <TrackedLinkPromotionWrapper key={index}>
                            <LinkItem onClick={itemOnClick} to={path.url}>
                              {label}
                            </LinkItem>
                          </TrackedLinkPromotionWrapper>
                        ) : null;
                      },
                    )}
                  </LinkWrapper>
                </AdditionalLinks>
              </Slide>
            )}
            {content.cta && (
              <Slide
                mounted={inView}
                duration={500}
                direction="up"
                transformLength={20}
              >
                <CallToAction
                  data-testid="teaserCtaLink"
                  variant="glassy"
                  size="fit-width"
                >
                  {content.cta}
                </CallToAction>
              </Slide>
            )}
          </TeaserTextWrapper>
        ) : null}
      </StyledWrapper>
    </Wrapper>
  );
};

const onlyWhenAnchor = (
  isAnchor: boolean,
  handler: React.MouseEventHandler | undefined,
): React.MouseEventHandler | undefined => (isAnchor ? handler : undefined);

const breakpoints = [
  {
    breakpoint: '(max-width: 767px)',
    columns: 2,
  },
  {
    breakpoint: '(max-width: 1023px)',
    columns: 3,
  },
  {
    columns: 1,
  },
];
const possibleImageWidths = [800, 960, 1200, 1400];
const possibleLogoWidths = [200, 400, 600];

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledLink = styled(UnstyledLinkLinaria)`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledWrapper = styled.div<{to?: RouterLinkTarget}>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const BackgroundWrapper = styled.div<{$fixedPosition: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  height: 100%;
  width: 100%;

  position: ${({$fixedPosition}) => ($fixedPosition ? 'absolute' : 'relative')};
`;

const StyledImage = styled(Image)<{$hasParallaxEffect: boolean}>`
  object-fit: cover;
  object-position: center top;
  position: absolute;
  width: 100%;
  height: ${({$hasParallaxEffect}) => ($hasParallaxEffect ? '150%' : '100%')};
`;

const TeaserTextWrapper = styled.div`
  position: absolute;
  overflow: hidden;
  top: 0;
  color: initial;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.33));
  gap: 4px;
`;

const Subline = styled.span`
  color: ${COLORS.White};
  text-transform: uppercase;
  text-transform: uppercase;
  font-size: ${FONT_SIZES.px14};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px18};
  `)}
`;

const Headline = styled.span`
  color: ${COLORS.White};
  text-align: center;
  width: 80%;
  word-break: break-word;
  font-size: ${FONT_SIZES.px20};
  line-height: 1.2;
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  ${tabletAndUpLinaria(`
    font-weight: ${FONT_WEIGHTS.bold};
    text-transform: uppercase;
    font-size: ${FONT_SIZES.px56};
    line-height: unset;
    width: 100%;
  `)}
`;

const StyledCenteredImage = styled(Image)`
  object-fit: contain;
  max-width: 60%;
  max-height: 30%;
  margin: 0 auto;

  ${mobileOnlyLinaria(`margin-top: 0.5rem;`)};
`;

const ContentWrapper = styled.div<{to?: RouterLinkTarget}>`
  display: contents;
`;

const CallToAction = styled(Button)`
  margin-top: 48px;
`;

const AdditionalLinks = styled.section`
  color: ${COLORS.White};
  position: absolute;
  left: 0px;
  bottom: 10px;
  width: 100%;
  overflow: hidden;
  padding-inline: 40px;
  white-space: pre;
  box-sizing: border-box;

  ${desktopAndUpLinaria('bottom: 30px;')};
`;

const StyledAdditionalLinkSpan = styled.span`
  display: block;
  font-weight: ${FONT_WEIGHTS.bold};
  font-size: ${FONT_SIZES.px20};
  padding-bottom: 20px;
`;

const LinkWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  list-style: none;
`;

const LinkItem = styled(UnstyledLinkLinaria)`
  color: ${COLORS.White};
  display: flex;
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.12px;
  text-decoration: none;
  border-bottom: 1px solid currentcolor;
  padding-bottom: 2px;
  white-space: nowrap;
`;
